import type { FC, PropsWithChildren } from 'react';
import { createContext, useState, useMemo, useContext, useEffect, useRef, useCallback, useLayoutEffect } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ThemeId } from '@/js/libs/ui/src/V2/Palette';
import { palette } from '@/js/libs/ui/src/V2/Palette';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage';
import { PREFERS_DARK_QUERY, parseThemeSetting, resolveThemeId, applyThemeToDom, checkPrefersDark } from './helpers';
import { type ThemeSetting, afterNextPaint } from './helpers';

type Ctx = {
  themeId: ThemeId;
  isSystem: boolean;
  isSwitching: boolean;
  checkTheme: (id: ThemeSetting) => boolean;
  setTheme: (id: ThemeSetting) => void;
};

const ThemeControllerContext = createContext<Ctx | null>(null);

export const ThemeControllerProvider: FC<PropsWithChildren> = ({ children }) => {
  const { loadString, saveString } = useLocalStorage();

  // TODO: extend logic with useIdentityUser once the backend is ready
  const savedThemeSetting = useMemo(() => {
    const raw = loadString(StorageKey.THEME_ID);
    return parseThemeSetting(raw);
  }, [loadString]);

  const initialThemeId = useMemo<ThemeId>(() => {
    return resolveThemeId(savedThemeSetting);
  }, [savedThemeSetting]);

  const [themeId, setThemeId] = useState<ThemeId>(initialThemeId);
  const [isSystem, setIsSystem] = useState<boolean>(savedThemeSetting === 'system');
  const [isPending, setIsPending] = useState<boolean>(false);
  const isInitialMountRef = useRef(true);

  const setTheme = useCallbackRef((id: ThemeSetting) => {
    const currentSetting: ThemeSetting = isSystem ? 'system' : themeId;
    if (id === currentSetting) return;

    saveString(StorageKey.THEME_ID, id);

    const newThemeId: ThemeId = id === 'system' ? (checkPrefersDark() ? 'primaryDark' : 'primary') : id;

    if (newThemeId === themeId) {
      setIsSystem(id === 'system');
      return;
    }

    setIsPending(true);

    afterNextPaint(() => {
      setThemeId(newThemeId);
      setIsSystem(id === 'system');
    });
  });

  // eslint-disable-next-line no-restricted-syntax
  const checkTheme = useCallback((id: ThemeSetting) => (id === 'system' ? isSystem : !isSystem && themeId === id), [themeId, isSystem]);

  useEffect(() => {
    if (!isSystem || typeof window === 'undefined' || !window.matchMedia) return () => {};

    const mql = window.matchMedia(PREFERS_DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      setThemeId(e.matches ? 'primaryDark' : 'primary');
    };

    const currentPrefersDark = mql.matches;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeId(currentPrefersDark ? 'primaryDark' : 'primary');

    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [isSystem]);

  useLayoutEffect(() => {
    const bg = palette[themeId].colors.grey05_1 ?? palette.primary.colors.grey05_1;

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      applyThemeToDom(themeId, bg);
      return () => {};
    }

    const root = document.documentElement;
    root.classList.add('theme-switching');
    applyThemeToDom(themeId, bg);

    const rafId = requestAnimationFrame(() => {
      root.classList.remove('theme-switching');
      setIsPending(false);
    });

    return () => {
      cancelAnimationFrame(rafId);
      root.classList.remove('theme-switching');
    };
  }, [themeId]);

  const value = useMemo(
    () => ({ themeId, isSystem, isSwitching: isPending, checkTheme, setTheme }),
    [themeId, isSystem, isPending, checkTheme, setTheme],
  );
  return <ThemeControllerContext.Provider value={value}>{children}</ThemeControllerContext.Provider>;
};

export const useThemeController = () => {
  const ctx = useContext(ThemeControllerContext);
  if (!ctx) throw new Error('useThemeController must be used within ThemeControllerProvider');
  return ctx;
};
